// src/components/SearchPicker.js
// A list you narrow by typing (David, 10/29): every word typed must appear in
// the name or number, in any order ("324089 parma", "parma 324"). While
// nothing is typed, suggested groups (e.g. "You're here") sit above the full
// list. onPick(item, source) says where the pick came from: the group's
// source, 'search' (typed), or 'list' (scrolled to it).
import React, { useMemo, useState } from 'react';

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

export default function SearchPicker({
  items,
  groups = [],
  getKey,
  getLabel,
  getSub = () => '',
  icon = '📍',
  onPick,
  selectedKey = null,
  placeholder = 'Type a name or number…',
  allTitle = 'All',
  emptyText = 'Nothing matches',
  status = null,
  autoFocus = false,
}) {
  const [q, setQ] = useState('');
  const words = norm(q).split(' ').filter(Boolean);

  const matches = useMemo(() => {
    if (!words.length) return items;
    return items.filter((it) => {
      const hay = norm(`${getLabel(it)} ${getSub(it) || ''}`);
      return words.every((w) => hay.includes(w));
    });
  }, [q, items]);

  const shownGroups = words.length ? [] : groups.filter((g) => g.items.length);

  const row = (it, source, note) => {
    const key = getKey(it);
    const sub = getSub(it);
    return (
      <div key={`${source}:${key}`} className="item-row" onClick={() => onPick(it, source)}>
        <div className="item-row-icon">{icon}</div>
        <div className="item-row-info">
          <h4>{getLabel(it)}</h4>
          {(sub || note) && <p>{[note, sub].filter(Boolean).join(' · ')}</p>}
        </div>
        {selectedKey === key
          ? <span className="picker-check" aria-label="Selected">✓</span>
          : (
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          )}
      </div>
    );
  };

  return (
    <div className="search-picker">
      <input
        className="input"
        type="search"
        placeholder={placeholder}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoFocus={autoFocus}
        style={{ marginBottom: 8 }}
      />
      {status && !words.length && <p className="picker-status">{status}</p>}

      {shownGroups.map((g) => (
        <div key={g.key} style={{ marginBottom: 12 }}>
          <p className="picker-group-title">{g.icon} {g.title}</p>
          <div className="card picker-group">
            {g.items.map((it) => row(it, g.source, g.note ? g.note(it) : null))}
          </div>
        </div>
      ))}

      <p className="picker-group-title">
        {words.length ? `${matches.length} match${matches.length === 1 ? '' : 'es'}` : (shownGroups.length ? `🔍 ${allTitle}` : allTitle)}
      </p>
      {matches.length === 0 ? (
        <div className="empty-state"><p>{emptyText}{q ? ` for "${q}"` : ''}</p></div>
      ) : (
        <div className="card">
          {matches.map((it) => row(it, words.length ? 'search' : 'list'))}
        </div>
      )}
    </div>
  );
}
